import { useEffect, useRef, useState } from "react";
import { ApiError, ApiSessionChangedError, type ApiClient } from "../api/api-client";
import { proofEligibilitySchema, type ProofEligibilityResponse } from "../api/schemas";
import { proofPreparationDecision, proofRetirementStatus, proofTierDisplay, type ProofDecisionContext } from "./mint-eligibility";
import { errorMessage } from "./model-helpers";
import { normalizeTierDisplay } from "./view-mappers";
import type { ProofEligibilityView } from "./ui-types";

interface Options {
  api: ApiClient;
  contextKey: string;
  readContextKey(): string;
  enabled: boolean;
  context: ProofDecisionContext;
  refreshRevision: number;
  subscribeProof(listener: () => void): () => void;
  readProofIdentity(): string;
}

/** Display guidance only. Action reads and POST lifetime belong to the mint hook. */
export function useProofEligibility({ api, contextKey, readContextKey, enabled, context, refreshRevision,
  subscribeProof, readProofIdentity }: Options): ProofEligibilityView {
  const [proofRevision, setProofRevision] = useState(0);
  const { proofId, challengeId } = context;
  const key = JSON.stringify([contextKey, proofId, challengeId, enabled, proofRevision]);
  const liveKey = useRef(key);
  liveKey.current = key;
  const generation = useRef(0);
  const activeReadAbort = useRef<AbortController | null>(null);
  const refreshRead = useRef<() => void>(() => {});
  const lastRefresh = useRef(refreshRevision);
  const committedRefresh = useRef(refreshRevision);
  const [stored, setStored] = useState<{ key: string; data: ProofEligibilityResponse | null;
    displayData: ProofEligibilityResponse | null; loading: boolean; error: string | null }>(
    { key: "", data: null, displayData: null, loading: false, error: null });
  const [, tick] = useState(0);
  useEffect(() => {
    let identity = readProofIdentity();
    return subscribeProof(() => {
      const next = readProofIdentity();
      if (identity === next) return;
      identity = next;
      generation.current++;
      activeReadAbort.current?.abort();
      setProofRevision((value) => value + 1);
    });
  }, [subscribeProof, readProofIdentity]);
  useEffect(() => { committedRefresh.current = refreshRevision; }, [refreshRevision]);
  useEffect(() => {
    const currentGeneration = ++generation.current;
    const abort = new AbortController();
    activeReadAbort.current = abort;
    lastRefresh.current = committedRefresh.current;
    setStored({ key, data: null, displayData: null, loading: enabled, error: null });
    if (!enabled) return () => abort.abort();
    let pending = false;
    let queued = false;
    let pollTimer: ReturnType<typeof setInterval> | undefined;
    let expiryTimer: ReturnType<typeof setInterval> | undefined;
    const current = () => !abort.signal.aborted && generation.current === currentGeneration &&
      liveKey.current === key && readContextKey() === contextKey;
    const load = (force = false) => {
      if (!current()) return;
      if (pending) { if (force) queued = true; return; }
      pending = true;
      setStored((previous) => previous.key === key ? { ...previous, loading: true } : previous);
      (async () => {
        try {
          const data = await api.get(`/mining/proofs/${proofId}/eligibility`, proofEligibilitySchema, abort.signal);
          if (!current()) return;
          if (data.proofId.toLowerCase() !== proofId.toLowerCase() || data.challengeId.toLowerCase() !== challengeId.toLowerCase()) {
            throw new Error("PROOF_CONTEXT_CHANGED");
          }
          // A mint-policy outage must not erase independently verified tier guidance.
          // This decision is display-only; action permission still uses current data below.
          const tiers = proofTierDisplay(data, { proofId, challengeId, matches: true }).usableTiers;
          setStored(previous => ({ key, data, loading: false, error: null,
            displayData: tiers !== null || data.proofStatus !== "VERIFIED" ? data : previous.displayData }));
        } catch (error) {
          const contextChanged = error instanceof ApiSessionChangedError ||
            error instanceof ApiError && error.status === 401 ||
            error instanceof Error && error.message === "PROOF_CONTEXT_CHANGED";
          if (current()) setStored(previous => ({ key, data: null, loading: false, error: errorMessage(error),
            displayData: contextChanged ? null : previous.displayData }));
        } finally {
          pending = false;
          if (queued && current()) { queued = false; load(); }
        }
      })().catch(() => console.error("Proof eligibility state could not be published."));
    };
    const refresh = () => load(true);
    refreshRead.current = refresh;
    const stopTimers = () => { clearInterval(pollTimer); clearInterval(expiryTimer); };
    const resume = () => {
      stopTimers();
      if (document.visibilityState === "hidden") return;
      tick((value) => value + 1);
      load(true);
      pollTimer = setInterval(() => load(), 12_000);
      expiryTimer = setInterval(() => tick((value) => value + 1), 1_000);
    };
    if (document.visibilityState === "hidden") load();
    resume();
    document.addEventListener("visibilitychange", resume);
    return () => {
      abort.abort(); stopTimers();
      if (activeReadAbort.current === abort) activeReadAbort.current = null;
      document.removeEventListener("visibilitychange", resume);
      if (refreshRead.current === refresh) refreshRead.current = () => {};
    };
  }, [api, key, enabled, contextKey, readContextKey, proofId, challengeId]);
  useEffect(() => {
    if (lastRefresh.current !== refreshRevision) {
      lastRefresh.current = refreshRevision;
      refreshRead.current();
    }
  }, [refreshRevision]);
  const state = stored.key === key ? stored : { data: null, displayData: null, loading: enabled, error: null };
  const decision = proofPreparationDecision(state.data, context);
  const displayDecision = proofTierDisplay(state.displayData, context);
  return { key, proofId: proofId || null, challengeId: challengeId || null,
    retirementStatus: !state.loading && !state.error ? proofRetirementStatus(state.data, context) : null,
    loading: state.loading, error: state.error, blockingReasons: state.data?.blockingReasons ?? (state.error ? [state.error] : []),
    decision, displayTiers: displayDecision.usableTiers,
    display: displayDecision.usableTiers === null ? null : state.displayData?.tierDisplay ? normalizeTierDisplay(state.displayData.tierDisplay) :
      displayDecision.source === "CURRENT" ? { status: "CURRENT", eligibleTiers: displayDecision.usableTiers, maxTierUsd: null,
        asOfBlock: state.displayData?.asOfBlock ?? null, asOfBlockHash: state.displayData?.asOfBlockHash ?? null,
        authorization: false, updating: decision.state !== "ALLOWED" } : null };
}
